import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Brush, Eraser, Trash2, Undo2 } from 'lucide-react';

interface MaskPainterProps {
  imageUrl: string;
  onMaskChange(blob: Blob | null): void;
}

export default function MaskPainter({ imageUrl, onMaskChange }: MaskPainterProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const maskRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [brushSize, setBrushSize] = useState(40);
  const [erasing, setErasing] = useState(false);
  const [ready, setReady] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const undoStackRef = useRef<Array<{ mask: ImageData; overlay: ImageData }>>([]);

  const initCanvases = useCallback(() => {
    const img = imgRef.current;
    const overlay = overlayRef.current;
    const mask = maskRef.current;
    if (!img || !overlay || !mask) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return;
    overlay.width = w;
    overlay.height = h;
    mask.width = w;
    mask.height = h;
    const mctx = mask.getContext('2d');
    const octx = overlay.getContext('2d');
    if (!mctx || !octx) return;
    mctx.fillStyle = 'black';
    mctx.fillRect(0, 0, w, h);
    octx.clearRect(0, 0, w, h);
    undoStackRef.current = [];
    setHasStrokes(false);
    setReady(true);
    onMaskChange(null);
  }, [onMaskChange]);

  useEffect(() => {
    setReady(false);
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      initCanvases();
    } else {
      img.addEventListener('load', initCanvases, { once: true });
    }
  }, [imageUrl, initCanvases]);

  const toCanvasCoords = useCallback((clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = ((clientX - rect.left) / rect.width) * overlay.width;
    const y = ((clientY - rect.top) / rect.height) * overlay.height;
    return { x, y };
  }, []);

  const paintSegment = useCallback((from: { x: number; y: number } | null, to: { x: number; y: number }) => {
    const mask = maskRef.current;
    const overlay = overlayRef.current;
    if (!mask || !overlay) return;
    const mctx = mask.getContext('2d');
    const octx = overlay.getContext('2d');
    if (!mctx || !octx) return;
    const radius = brushSize / 2;

    const drawOn = (ctx: CanvasRenderingContext2D, color: string, composite: GlobalCompositeOperation) => {
      ctx.save();
      ctx.globalCompositeOperation = composite;
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = brushSize;
      ctx.beginPath();
      ctx.arc(to.x, to.y, radius, 0, Math.PI * 2);
      ctx.fill();
      if (from) {
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
      ctx.restore();
    };

    if (erasing) {
      drawOn(mctx, 'black', 'source-over');
      drawOn(octx, 'rgba(0,0,0,1)', 'destination-out');
    } else {
      drawOn(mctx, 'white', 'source-over');
      drawOn(octx, 'rgba(244, 114, 182, 0.55)', 'source-over');
    }
  }, [brushSize, erasing]);

  const snapshotForUndo = useCallback(() => {
    const mask = maskRef.current;
    const overlay = overlayRef.current;
    if (!mask || !overlay) return;
    const mctx = mask.getContext('2d');
    const octx = overlay.getContext('2d');
    if (!mctx || !octx) return;
    undoStackRef.current.push({
      mask: mctx.getImageData(0, 0, mask.width, mask.height),
      overlay: octx.getImageData(0, 0, overlay.width, overlay.height),
    });
    if (undoStackRef.current.length > 20) undoStackRef.current.shift();
  }, []);

  const commitMask = useCallback(() => {
    const mask = maskRef.current;
    if (!mask) {
      onMaskChange(null);
      return;
    }
    if (!hasStrokes) {
      onMaskChange(null);
      return;
    }
    mask.toBlob(blob => onMaskChange(blob), 'image/png');
  }, [hasStrokes, onMaskChange]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!ready) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drawingRef.current = true;
    snapshotForUndo();
    const p = toCanvasCoords(e.clientX, e.clientY);
    if (!p) return;
    lastPointRef.current = null;
    paintSegment(null, p);
    lastPointRef.current = p;
    setHasStrokes(true);
  }, [ready, snapshotForUndo, toCanvasCoords, paintSegment]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const p = toCanvasCoords(e.clientX, e.clientY);
    if (!p) return;
    paintSegment(lastPointRef.current, p);
    lastPointRef.current = p;
  }, [toCanvasCoords, paintSegment]);

  const handlePointerUp = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    commitMask();
  }, [commitMask]);

  const handleClear = useCallback(() => {
    const mask = maskRef.current;
    const overlay = overlayRef.current;
    if (!mask || !overlay) return;
    const mctx = mask.getContext('2d');
    const octx = overlay.getContext('2d');
    if (!mctx || !octx) return;
    snapshotForUndo();
    mctx.fillStyle = 'black';
    mctx.fillRect(0, 0, mask.width, mask.height);
    octx.clearRect(0, 0, overlay.width, overlay.height);
    setHasStrokes(false);
    onMaskChange(null);
  }, [snapshotForUndo, onMaskChange]);

  const handleUndo = useCallback(() => {
    const snap = undoStackRef.current.pop();
    if (!snap) return;
    const mask = maskRef.current;
    const overlay = overlayRef.current;
    if (!mask || !overlay) return;
    const mctx = mask.getContext('2d');
    const octx = overlay.getContext('2d');
    if (!mctx || !octx) return;
    mctx.putImageData(snap.mask, 0, 0);
    octx.putImageData(snap.overlay, 0, 0);
    // Recompute hasStrokes by looking at any non-black pixel in mask
    const data = mctx.getImageData(0, 0, mask.width, mask.height).data;
    let painted = false;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > 0) { painted = true; break; }
    }
    setHasStrokes(painted);
    if (painted) commitMask();
    else onMaskChange(null);
  }, [commitMask, onMaskChange]);

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={containerRef}
        className="relative rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--sage-surface)' }}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt="mask target"
          className="block w-full h-auto select-none pointer-events-none"
          draggable={false}
        />
        <canvas
          ref={overlayRef}
          className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ touchAction: 'none' }}
        />
        <canvas ref={maskRef} className="hidden" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setErasing(false)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors"
          style={{
            backgroundColor: !erasing ? 'var(--sage-primary)' : 'var(--sage-bg)',
            color: !erasing ? '#1c1b1a' : 'var(--sage-text-muted)',
            border: '1px solid var(--sage-white-10)',
          }}
        >
          <Brush size={13} /> Paint
        </button>
        <button
          onClick={() => setErasing(true)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors"
          style={{
            backgroundColor: erasing ? 'var(--sage-primary)' : 'var(--sage-bg)',
            color: erasing ? '#1c1b1a' : 'var(--sage-text-muted)',
            border: '1px solid var(--sage-white-10)',
          }}
        >
          <Eraser size={13} /> Erase
        </button>
        <button
          onClick={handleUndo}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs"
          style={{
            backgroundColor: 'var(--sage-bg)',
            color: 'var(--sage-text-muted)',
            border: '1px solid var(--sage-white-10)',
          }}
        >
          <Undo2 size={13} /> Undo
        </button>
        <button
          onClick={handleClear}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs"
          style={{
            backgroundColor: 'var(--sage-bg)',
            color: 'var(--sage-text-muted)',
            border: '1px solid var(--sage-white-10)',
          }}
        >
          <Trash2 size={13} /> Clear
        </button>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--sage-text-muted)' }}>
          Brush
        </span>
        <input
          type="range"
          min={4}
          max={120}
          value={brushSize}
          onChange={e => setBrushSize(parseInt(e.target.value, 10))}
          className="flex-1"
        />
        <span className="text-xs tabular-nums w-8 text-right" style={{ color: 'var(--sage-text)' }}>
          {brushSize}
        </span>
      </div>
    </div>
  );
}
